import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../../config/api';
import './Drivers.css';

const Drivers = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [driverGroups, setDriverGroups] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({
    name: '',
    type: 'inhouse',
    restaurantId: '',
    storeId: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, driversRes, restaurantsRes, storesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/delivery/driver-groups`),
        axios.get(`${API_URL}/admin/delivery/drivers`),
        axios.get(`${API_URL}/admin/restaurants`),
        axios.get(`${API_URL}/admin/stores`)
      ]);
      setDriverGroups(groupsRes.data);
      setDrivers(driversRes.data);
      setRestaurants(restaurantsRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError(t('admin.errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({
      name: '',
      type: 'inhouse',
      restaurantId: '',
      storeId: ''
    });
    setShowGroupModal(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      type: group.type,
      restaurantId: group.restaurant?._id || '',
      storeId: group.store?._id || ''
    });
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (editingGroup) {
        await axios.patch(`${API_URL}/admin/delivery/driver-groups/${editingGroup._id}`, {
          name: groupForm.name,
          isActive: true
        });
      } else {
        await axios.post(`${API_URL}/admin/delivery/driver-groups`, groupForm);
      }
      setShowGroupModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving driver group:', error);
      showError(error.response?.data?.message || t('admin.errorSavingDriverGroup'));
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm(t('admin.confirmDeleteDriverGroup'))) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/admin/delivery/driver-groups/${groupId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting driver group:', error);
      showError(error.response?.data?.message || t('admin.errorDeletingDriverGroup'));
    }
  };

  const handleAssignDriver = async (groupId, driverId) => {
    try {
      await axios.post(`${API_URL}/admin/delivery/driver-groups/${groupId}/drivers/${driverId}`);
      fetchData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      showError(error.response?.data?.message || t('admin.errorAssigningDriver'));
    }
  };

  const handleRemoveDriver = async (groupId, driverId) => {
    try {
      await axios.delete(`${API_URL}/admin/delivery/driver-groups/${groupId}/drivers/${driverId}`);
      fetchData();
    } catch (error) {
      console.error('Error removing driver:', error);
      showError(error.response?.data?.message || t('admin.errorRemovingDriver'));
    }
  };

  const unassignedDrivers = drivers.filter(d => {
    const groups = d.driverGroups || (d.driverGroup ? [d.driverGroup] : []);
    return groups.length === 0;
  });

  const driversNotInGroup = (group) => {
    const driverIdsInGroup = (group.drivers || []).map(dr => (dr._id || dr).toString());
    return drivers.filter(d => !driverIdsInGroup.includes((d._id || d).toString()));
  };

  if (loading) {
    return <div className="loading-spinner">{t('admin.loading')}</div>;
  }

  return (
    <div className="drivers-management">
      <div className="drivers-header">
        <h2>{t('admin.driverManagement')}</h2>
        <button className="btn btn-primary" onClick={handleCreateGroup}>
          + {t('admin.createDriverGroup')}
        </button>
      </div>

      <div className="driver-groups-section">
        <h3>{t('admin.driverGroups')}</h3>
        <div className="driver-groups-grid">
          {driverGroups.map((group) => (
            <div key={group._id} className="driver-group-card">
              <div className="group-header">
                <div>
                  <h4>{group.name}</h4>
                  <span className="group-type-badge">{group.type}</span>
                  {group.restaurant && (
                    <p className="group-entity">{t('admin.restaurant')}: {group.restaurant.name}</p>
                  )}
                  {group.store && (
                    <p className="group-entity">{t('admin.store')}: {group.store.name}</p>
                  )}
                </div>
                <div className="group-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEditGroup(group)}
                  >
                    {t('admin.edit')}
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteGroup(group._id)}
                  >
                    {t('admin.delete')}
                  </button>
                </div>
              </div>
              <div className="group-drivers">
                <h5>{t('admin.deliveryDriver')} ({group.drivers?.length || 0})</h5>
                {group.drivers && group.drivers.length > 0 ? (
                  <div className="drivers-list">
                    {group.drivers.map((driver) => (
                      <div key={driver._id} className="driver-item">
                        <span>{driver.firstName} {driver.lastName}</span>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveDriver(group._id, driver._id)}
                        >
                          {t('admin.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-drivers">{t('admin.noDriversAssigned')}</p>
                )}
                <div className="add-driver-to-group">
                  <select
                    className="assign-select"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignDriver(group._id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">{t('admin.addDriverToGroup') || 'Add driver to this group'}</option>
                    {driversNotInGroup(group).map((driver) => (
                      <option key={driver._id} value={driver._id}>
                        {driver.firstName} {driver.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="unassigned-drivers-section">
        <h3>{t('admin.unassignedDrivers')}</h3>
        {unassignedDrivers.length > 0 ? (
          <div className="unassigned-drivers-list">
            {unassignedDrivers.map((driver) => (
              <div key={driver._id} className="unassigned-driver-item">
                <span>{driver.firstName} {driver.lastName} ({driver.email})</span>
                <select
                  className="assign-select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAssignDriver(e.target.value, driver._id);
                    }
                  }}
                >
                  <option value="">{t('admin.assignToGroup')}</option>
                  {driverGroups.map((group) => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-unassigned">{t('admin.allDriversAssigned')}</p>
        )}
      </div>

      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingGroup ? t('admin.editDriverGroup') : t('admin.createDriverGroup')}</h3>
              <button className="close-btn" onClick={() => setShowGroupModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('admin.groupName')}</label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder={t('admin.enterGroupName')}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.groupType')}</label>
                <select
                  value={groupForm.type}
                  onChange={(e) => setGroupForm({ ...groupForm, type: e.target.value, restaurantId: '', storeId: '' })}
                  disabled={editingGroup !== null}
                >
                  <option value="inhouse">{t('admin.inhouseDrivers')}</option>
                  <option value="restaurant">{t('admin.restaurantDrivers')}</option>
                  <option value="store">{t('admin.storeDrivers')}</option>
                </select>
              </div>
              {groupForm.type === 'restaurant' && (
                <div className="form-group">
                  <label>{t('admin.restaurant')}</label>
                  <select
                    value={groupForm.restaurantId}
                    onChange={(e) => setGroupForm({ ...groupForm, restaurantId: e.target.value })}
                    disabled={editingGroup !== null}
                  >
                    <option value="">{t('admin.selectRestaurant')}</option>
                    {restaurants.map((restaurant) => (
                      <option key={restaurant._id} value={restaurant._id}>
                        {restaurant.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {groupForm.type === 'store' && (
                <div className="form-group">
                  <label>{t('admin.store')}</label>
                  <select
                    value={groupForm.storeId}
                    onChange={(e) => setGroupForm({ ...groupForm, storeId: e.target.value })}
                    disabled={editingGroup !== null}
                  >
                    <option value="">{t('admin.selectStore')}</option>
                    {stores.map((store) => (
                      <option key={store._id} value={store._id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>
                {t('admin.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveGroup}>
                {editingGroup ? t('admin.update') : t('admin.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
